<template>
  <div>
    <div class="mb-2">
      <button class="btn btn-primary btn-sm" @click="openProductsSelectList">Dodaj produkty</button>
    </div>

    <div class="category-products-stage">
      <div class="row">
        <SearchBar2
            :searchOptions="fieldsAvailableForSearch"
            @searchRequest="searchOnList($event)"
        />
      </div>

      <div class="row mt-2">
        <CommonTable2
            :table="tableData"
            rowIdentifier="guid"
            @onItemAction="runTableAction($event)"
            @onMassAction="runMassAction($event)"
            @onSort="sort($event)"
            :key="refreshTable"
            :currentPage="currentPage"
            :totalRows="itemsTotalElements"
            :totalPages="itemsTotalPages"
            :showPaginationTop="true"
            :showPaginationBottom="true"
            @onPageChange="currentPage = $event"
            :sortColumn="sortingKey"
            :sortDirection="sortingDirection"
            :showMassActions="showMassActions"
        />
      </div>

      <!-- Nested modal to pick products to add into category -->
      <ProductListModal
          :showModal="showProductsSelectList"
          @update:showModal="showProductsSelectList = $event"
          title="Baza centralnych towarów"
          ok-title="Dodaj"
          :multiselect="true"
          @onApply="addProductToCategory($event)"
          :showMassActions="true"
          :showRowActions="true"
          :resetSelectionAfterApply="true"
      />
    </div>
  </div>
</template>

<script>
import SearchBar2 from "@/components/widgets/SearchBar2.vue";
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import ProductListModal from "@/views/DrugstoreCommonManager/ProductListModal.vue";
import Swal from "sweetalert2";
import {useSimplepharmAuthModule} from "@/state/pinia/simplepharmAuthModule";
import {useCommonAppModule} from "@/state/pinia/commonAppModule";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";

export default {
  name: "CategoryProductDataList",
  components: { SearchBar2, CommonTable2, ProductListModal },
  props: {
    categoryGuid: {
      type: String,
      required: true
    },
    showMassActions: {
      type: Boolean,
      default: () => true
    },
  },
  data() {
    return {
      tableData: {},
      currentPage: 1,
      itemsTotalPages: 1,
      itemsTotalElements: 0,
      sortingDirection: "ASC",
      sortingKey: "name",
      refreshTable: true,
      showProductsSelectList: false,
      searchRequest: {
        searchPhrase: null,
        searchKey: null,
      },
      fieldsAvailableForSearch: [
        { name: "EAN", key: "ean" },
        { name: "Nazwa towaru", key: "name" },
        { name: "Producent", key: "manufacturer" },
      ],
      tableHeaders: [
        { text: "Nazwa", key: "name", type: "text", sortable: true },
        { text: "EAN", key: "ean", type: "text", sortable: true },
        { text: "Producent", key: "manufacturer", type: "text", sortable: true },
        { text: "Akcje", key: "actions", type: "actions", actions: [
            {
              text: "",
              icon: "fas fa-trash",
              action: "remove-product-from-category",
              class: "btn btn-danger btn-sm table-actions-button edit-button",
              enabled: true,
              tooltip: "Usuń z kategorii",
            }
        ] },
      ],
      tableMassActions: [
        {
          text: "Usuń zaznaczone",
          icon: "fas fa-trash",
          action: "remove-products-from-category",
          class: "btn btn-danger btn-sm",
          enabled: true,
          tooltip: "Usuń zaznaczone produkty z kategorii",
        }
      ],
    };
  },
  computed:{
    simplepharmAuth(){
      return useSimplepharmAuthModule();
    },
    commonModule(){
      return useCommonAppModule();
    },
    lazyloaderModule(){
      return useLazyLoaderModule();
    },
  },
  methods: {

    openProductsSelectList() {
      this.showProductsSelectList = true;
    },

    searchOnList(searchRequest) {
      this.searchRequest = searchRequest;
      this.refreshList();
    },

    sort(sortKey) {
      this.sortingKey = sortKey;
      this.refreshList();
      this.toggleSortingDirection();
    },
    toggleSortingDirection() {
      this.sortingDirection = this.sortingDirection === "ASC" ? "DESC" : "ASC";
    },

    runTableAction(event) {
      if (event.action.action === "remove-product-from-category") {
        this.removeProductFromCategory([event.itemData]);
      }
    },
    runMassAction(event) {
      if (event.action.action === "remove-products-from-category") {
        this.removeProductFromCategory(event.selectedItems);
      }
    },

    // Required methods by spec
    addProductToCategory(selectedProducts) {
      if (!this.categoryGuid) return;
      const payload = {
        categoryGuid: this.categoryGuid,
        productsGuids: (selectedProducts || []).map(p => p.guid)
      };
      if (!payload.productsGuids || payload.productsGuids.length === 0) return;
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Dodawanie produktów do kategorii...");
      this.commonModule.httpAddProductsToCategory(payload).then(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
        this.showProductsSelectList = false;
        this.refreshList();
      }).catch(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    },
    removeProductFromCategory(products) {
      if (!products || products.length === 0) return;
      Swal.fire({
        title: "Czy na pewno chcesz usunąć zaznaczone produkty z kategorii?",
        text: "Tej operacji nie można cofnąć",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj",
        customClass: { container: "swal-highest" },
      }).then((result) => {
        if (result.isConfirmed) {
          const payload = {
            categoryGuid: this.categoryGuid,
            productsGuids: products.map(product => product.guid)
          };
          this.lazyloaderModule.setDisplayLazyLoader(true);
          this.lazyloaderModule.setProgressBarMessage("Usuwanie produktów z kategorii...");
          this.commonModule.httpRemoveProductsFromCategory(payload).then(() => {
            this.lazyloaderModule.setDisplayLazyLoader(false);
            this.refreshList();
          }).catch(() => {
            this.lazyloaderModule.setDisplayLazyLoader(false);
          });
        }
      });
    },

    refreshList() {
      this.lazyloaderModule.setDisplayLazyLoader(true);
      this.lazyloaderModule.setProgressBarMessage("Pobieranie listy produktów w kategorii");
      const payload = {
        categoryGuid: this.categoryGuid,
        currentPage: this.currentPage,
        sortingDirection: this.sortingDirection,
        sortingKey: this.sortingKey,
        searchAndFilterRequest: {
          fields: null,
          search: this.searchRequest,
          filters: null,
        }
      };
      this.refreshTable = false;
      this.commonModule.httpGetCategoryProductsPaging(payload).then((response) => {
        const page = response.data.payload;
        const items = page.content || [];
        this.itemsTotalElements = page.totalElements || 0;
        this.itemsTotalPages = page.totalPages || 1;

        // Build table data
        this.tableData = {
          headers: this.tableHeaders,
          massActions: this.tableMassActions,
          data: items.map(p => ({
            ...p,
            actions: [] // disable defaults, CommonTable uses provided actions header
          }))
        };
        this.refreshTable = true;
        this.lazyloaderModule.setDisplayLazyLoader(false);
      }).catch(() => {
        this.lazyloaderModule.setDisplayLazyLoader(false);
      });
    },
  },
  watch: {
    currentPage() {
      this.refreshList();
    },
    categoryGuid: {
      handler() {
        // Reset to first page when category changes
        this.currentPage = 1;
        this.refreshList();
      },
      immediate: true,
    }
  },
};
</script>

<style scoped>
.category-products-stage {
  margin-top: 8px;
}
</style>
